/**
 * Error Handler Module
 *
 * Provides standardized error handling and response formatting
 * for Firebase services.
 */

/**
 * Create a standardized success response
 * @param {any} data - Response data
 * @returns {Object} - Standardized success response object
 */
export function createSuccessResponse(data) {
  return {
    success: true,
    data: typeof data === "undefined" ? null : data,
    error: null,
  };
}

/**
 * Create a standardized error response
 * @param {Error|string} error - Error object or message
 * @param {string} [source] - Source of the error (function name)
 * @returns {Object} - Standardized error response object
 */
export function createErrorResponse(error, source = "unknown") {
  const message = normalizeErrorMessage(error);

  // Log the error with source for debugging
  console.error(`Error in ${source}:`, error);

  return {
    success: false,
    data: null,
    error: message,
  };
}

function normalizeErrorMessage(error) {
  if (!error) {
    return "Unknown error occurred";
  }

  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    return error.message || "Unknown error occurred";
  }

  if (typeof error === "object") {
    if (error.message && typeof error.message === "string") {
      return error.message;
    }

    try {
      return JSON.stringify(error);
    } catch (jsonError) {
      console.warn("Unable to serialize error object", jsonError);
    }
  }

  return String(error);
}

/**
 * Create a retry handler function with exponential backoff
 * @param {Function} operation - The operation to retry
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} baseDelay - Base delay in milliseconds
 * @param {Function} isRetryable - Function to determine if error is retryable
 * @returns {Function} - Retry handler function
 */
export function createRetryHandler(
  operation,
  maxRetries = 3,
  baseDelay = 100,
  isRetryable = null
) {
  return async function executeWithRetry(...args) {
    let retryCount = 0;

    // Default retryable check if none provided
    const shouldRetry =
      isRetryable ||
      ((error) => {
        // List of common retryable error codes
        const retryableCodes = [
          "unavailable",
          "deadline-exceeded",
          "cancelled",
          "resource-exhausted",
          "internal",
          "aborted",
          //'already-exists' // Added to handle transaction conflicts
        ];

        return error && error.code && retryableCodes.includes(error.code);
      });

    while (true) {
      try {
        return await operation(...args);
      } catch (error) {
        // Don't retry if we've hit max retries or error isn't retryable
        if (retryCount >= maxRetries || !shouldRetry(error)) {
          throw error;
        }

        // Increment retry count
        retryCount++;

        // Calculate delay with exponential backoff and jitter
        const delay = Math.floor(
          Math.random() * (baseDelay * Math.pow(2, retryCount))
        );

        console.warn(
          `Transient error, retrying (${retryCount}/${maxRetries}) in ${delay}ms:`,
          error.message || error
        );

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  };
}

/**
 * Normalize Firebase error for consistent handling
 * @param {Error} error - The error to normalize
 * @returns {Error} - Normalized error
 */
export function normalizeFirebaseError(error) {
  // Already normalized
  if (error.normalized) {
    return error;
  }

  // Create a new error with more user-friendly message
  const normalizedError = new Error(getUserFriendlyErrorMessage(error));

  // Copy properties
  normalizedError.code = error.code;
  normalizedError.name = error.name;
  normalizedError.original = error;
  normalizedError.normalized = true;

  return normalizedError;
}

/**
 * Get a user-friendly error message
 * @param {Error} error - The error
 * @returns {string} - User-friendly error message
 */
function getUserFriendlyErrorMessage(error) {
  // No error
  if (!error) {
    return "An unknown error occurred";
  }

  // Already a string
  if (typeof error === "string") {
    return error;
  }

  // Map common Firebase error codes to user-friendly messages
  const errorMessages = {
    // Auth errors
    "auth/user-not-found": "The email address is not registered",
    "auth/wrong-password": "The password is invalid",
    "auth/email-already-in-use": "This email is already in use",
    "auth/weak-password": "The password must be at least 6 characters",
    "auth/invalid-email": "The email address is not valid",
    "auth/account-exists-with-different-credential":
      "An account already exists with the same email",
    "auth/popup-closed-by-user":
      "The sign-in popup was closed before completing",
    "auth/unauthorized-domain":
      "This domain is not authorized for OAuth operations",
    "auth/operation-not-allowed": "This operation is not allowed",
    "auth/requires-recent-login":
      "Please sign in again to complete this operation",

    // Firestore errors
    "permission-denied": "You do not have permission to access this resource",
    "not-found": "The requested document was not found",
    "already-exists": "The document already exists",
    "resource-exhausted":
      "Database operation limit exceeded, please try again later",
    "failed-precondition": "The operation failed because the data has changed",
    unavailable: "The service is currently unavailable, please try again later",

    // Functions errors
    "functions/invalid-argument": "Invalid data was provided to this operation",
    "functions/unauthenticated": "You must be logged in to perform this action",
    "functions/permission-denied":
      "You do not have permission to perform this action",
    "functions/internal": "An internal error occurred, please try again later",

    // Network errors
    "network-request-failed":
      "A network error occurred, please check your connection",

    // Generic errors
    "deadline-exceeded": "The operation timed out, please try again",
    cancelled: "The operation was cancelled",
    unknown: "An unknown error occurred",
  };

  // Use mapped message if available
  if (error.code && errorMessages[error.code]) {
    return errorMessages[error.code];
  }

  // Fall back to error message or default
  return error.message || "An unexpected error occurred";
}
